import { FC, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import propTypes from 'prop-types';
import { Box } from '@material-ui/core';
import Uppy from '@uppy/core';
import { StatusBar } from '@uppy/react';
import type { SxProps } from '@material-ui/system';
import { Camera } from '../contexts/settings-context';
import { BBox } from '../utils/card-crop';
import { ManualCropOverlay } from './manual-crop-overlay';

import '@uppy/core/dist/style.css';
import '@uppy/status-bar/dist/style.css';

export const CAMERA_RATIO = 4024 / 3036;

interface CameraViewerProps {
  camera: Camera;
  uppy?: Uppy;
  sx?: SxProps;
  /** Clockwise rotation (deg, 0/90/180/270) applied to the live preview for framing. */
  rotate?: number;
  /** Manual-crop mode: show a draggable crop rectangle over the stream. */
  manual?: boolean;
  /** The manual crop box (native image coords). Seeded from a one-off detection when null. */
  manualBox?: BBox | null;
  /** Called as the operator drags the manual box, and once to seed it. */
  onManualBox?: (box: BBox) => void;
  /** Called with the final box when a drag ends (or once on seed), for persisting the position. */
  onManualBoxCommit?: (box: BBox) => void;
  /** Reports the live stream image's natural pixel size (for measuring the manual box). */
  onFrameSize?: (w: number, h: number) => void;
}

// Magnifier loupe shown while dragging a corner, so the operator can hit the item edge precisely.
const LOUPE = 132; // px (canvas + circle diameter)
const LOUPE_ZOOM = 3;

/** The letterboxed image rect within the rotating wrapper, plus the image's native size. */
interface RectState {
  left: number;
  top: number;
  width: number;
  height: number;
  natW: number;
  natH: number;
}

/** The actual (letterboxed) image rect inside an object-fit:contain <img>. */
const containedRect = (img: HTMLImageElement) => {
  const cw = img.clientWidth;
  const ch = img.clientHeight;
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  if (!nw || !nh) return { left: 0, top: 0, width: cw, height: ch };
  const scale = Math.min(cw / nw, ch / nh);
  const w = nw * scale;
  const h = nh * scale;
  return { left: (cw - w) / 2, top: (ch - h) / 2, width: w, height: h };
};

export const CameraViewer: FC<CameraViewerProps> = ({
  camera,
  uppy,
  sx,
  rotate = 0,
  manual = false,
  manualBox = null,
  onManualBox,
  onManualBoxCommit,
  onFrameSize,
}) => {
  const height = 500;
  const width = Math.floor(height * CAMERA_RATIO);
  const imgRef = useRef<HTMLImageElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  // Measure the container so a quarter-turn preview can be sized to fit within it (a 90/270
  // rotation swaps the image's width/height, which would otherwise overflow the capture button).
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  const quarterTurn = rotate === 90 || rotate === 270;
  useEffect(() => {
    const el = outerRef.current;
    if (!el || !rotate || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      setContainerSize({ w: el.clientWidth, h: el.clientHeight });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [rotate]);

  // The rotating wrapper. For a quarter turn we give it the container's SWAPPED dimensions and
  // absolutely centre it, so after rotating it fills the container exactly (no overflow); the
  // image (object-fit: contain) letterboxes inside. Non-quarter turns rotate in place.
  const { w: cw, h: ch } = containerSize;
  const wrapperSx: SxProps = useMemo(() => {
    if (!rotate || (quarterTurn && (!cw || !ch))) {
      return {
        position: 'relative',
        height: '100%',
        maxWidth: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...(rotate ? { transform: `rotate(${rotate}deg)` } : {}),
      };
    }
    if (!quarterTurn) {
      return {
        position: 'relative',
        height: '100%',
        maxWidth: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transform: `rotate(${rotate}deg)`,
      };
    }
    return {
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: ch,
      height: cw,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
    };
  }, [rotate, quarterTurn, cw, ch]);
  const fillWrapper = quarterTurn && Boolean(cw) && Boolean(ch);

  // Magnifier loupe: while a corner is dragged, draw a zoomed view of the stream around the corner
  // (native coords, held in a ref so the rAF loop reads the latest without restarting each move).
  const loupeCanvasRef = useRef<HTMLCanvasElement>(null);
  const loupePointRef = useRef<{ x: number; y: number } | null>(null);
  const [loupeActive, setLoupeActive] = useState(false);
  const [loupeSide, setLoupeSide] = useState<'left' | 'right'>('right');
  const handleLoupe = useCallback((pt: { x: number; y: number } | null) => {
    loupePointRef.current = pt;
    setLoupeActive(Boolean(pt));
    const img = imgRef.current;
    // Show the loupe on the side away from the corner being dragged, to avoid occlusion.
    if (pt && img?.naturalWidth) setLoupeSide(pt.x < img.naturalWidth / 2 ? 'right' : 'left');
  }, []);
  useEffect(() => {
    if (!loupeActive) return undefined;
    let raf = 0;
    const draw = () => {
      const img = imgRef.current;
      const canvas = loupeCanvasRef.current;
      const ctx = canvas?.getContext('2d');
      const pt = loupePointRef.current;
      if (img && img.naturalWidth && canvas && ctx && pt) {
        const s = LOUPE / LOUPE_ZOOM; // source sample size (native px)
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, LOUPE, LOUPE);
        try {
          ctx.drawImage(img, pt.x - s / 2, pt.y - s / 2, s, s, 0, 0, LOUPE, LOUPE);
        } catch {
          /* stream frame not ready yet */
        }
        // Crosshair marks the exact corner point.
        ctx.strokeStyle = 'rgba(255,64,64,0.9)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(LOUPE / 2, 0);
        ctx.lineTo(LOUPE / 2, LOUPE);
        ctx.moveTo(0, LOUPE / 2);
        ctx.lineTo(LOUPE, LOUPE / 2);
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [loupeActive]);

  // Manual mode: track the letterboxed image rect (position + size + native size) so the
  // draggable crop overlay can be placed over the preview and mapped to native coords.
  const [imgRect, setImgRect] = useState<RectState | null>(null);
  // Keep `recomputeRect` stable regardless of the (often inline) onFrameSize callback, so the
  // manual effect below doesn't re-run every render — which would stop imgRect/seed from settling.
  const onFrameSizeRef = useRef(onFrameSize);
  onFrameSizeRef.current = onFrameSize;
  const recomputeRect = useCallback(() => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return;
    const r = containedRect(img);
    setImgRect({
      left: img.offsetLeft + r.left,
      top: img.offsetTop + r.top,
      width: r.width,
      height: r.height,
      natW: img.naturalWidth,
      natH: img.naturalHeight,
    });
    onFrameSizeRef.current?.(img.naturalWidth, img.naturalHeight);
  }, []);
  useEffect(() => {
    if (!manual) {
      setImgRect(null);
      return undefined;
    }
    recomputeRect();
    const el = imgRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(recomputeRect);
    ro.observe(el);
    return () => ro.disconnect();
  }, [manual, recomputeRect]);

  // Seed a centred box at 80% of the frame when nothing usable is saved, then persist it across
  // toggles/sessions.
  //
  // The saved box is absolute pixels in whatever frame it was drawn on, and nothing records which
  // frame that was. The same camera id can stream at a different resolution in a later session (a
  // real device vs a dev/mock stream), so a restored box can be many times the current frame —
  // rendering far larger than the preview with its handles off-screen. Treat "doesn't fit the
  // current frame" the same as "nothing saved" and re-seed rather than restoring nonsense.
  useEffect(() => {
    if (!manual || !onManualBox || !imgRect) return;
    const { natW, natH } = imgRect;
    const EPS = 1; // saved boxes can carry sub-pixel drift from a drag
    const fits =
      manualBox &&
      manualBox.w > 0 &&
      manualBox.h > 0 &&
      manualBox.x >= -EPS &&
      manualBox.y >= -EPS &&
      manualBox.x + manualBox.w <= natW + EPS &&
      manualBox.y + manualBox.h <= natH + EPS;
    if (fits) return;
    const w = Math.round(natW * 0.8);
    const h = Math.round(natH * 0.8);
    const seedBox: BBox = {
      x: Math.round((natW - w) / 2),
      y: Math.round((natH - h) / 2),
      w,
      h,
    };
    onManualBox(seedBox);
    onManualBoxCommit?.(seedBox);
  }, [manual, manualBox, onManualBox, onManualBoxCommit, imgRect]);

  const streamUrl = useMemo(
    () => `${camera.url}/photos/stream?width=${width}&height=${height}`,
    [camera.url],
  );

  return (
    <Box
      ref={outerRef}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        // Allow the feed to shrink within a height-bounded parent (so the capture
        // button below it stays visible); falls back to the native size otherwise.
        flex: '1 1 auto',
        minHeight: 0,
        position: 'relative',
        ...sx,
      }}
    >
      {/* Rotating wrapper: img + manual crop overlay turn together so the box stays aligned.
          The spinner and status bar below are outside it, so they stay upright. */}
      <Box sx={wrapperSx}>
        <img
          ref={imgRef}
          // Lets the loupe read frames off the stream onto a canvas (camera sends CORS: *).
          crossOrigin={manual ? 'anonymous' : undefined}
          onLoad={() => {
            if (manual) recomputeRect();
          }}
          src={streamUrl}
          alt={`camera stream ${camera.id}`}
          style={{
            // Fill the available (parent) height so the feed is as large as possible,
            // scaling with the viewport; contain keeps the aspect ratio (letterboxed).
            // On a quarter turn the wrapper is sized to the swapped container box, so the
            // image fills it (width + height) and letterboxes via object-fit.
            height: '100%',
            width: fillWrapper ? '100%' : undefined,
            maxHeight: '100%',
            maxWidth: '100%',
            objectFit: 'contain',
            borderRadius: 4,
            boxShadow: '0 0 3px 1px rgba(0,0,0,0.75)',
          }}
        />
        {manual && imgRect && manualBox && onManualBox && (
          <Box
            sx={{
              position: 'absolute',
              left: imgRect.left,
              top: imgRect.top,
              width: imgRect.width,
              height: imgRect.height,
            }}
          >
            <ManualCropOverlay
              box={manualBox}
              scale={imgRect.width / imgRect.natW}
              bounds={{ w: imgRect.natW, h: imgRect.natH }}
              rotate={rotate}
              onChange={onManualBox}
              onCommit={onManualBoxCommit}
              onLoupe={handleLoupe}
            />
          </Box>
        )}
      </Box>
      {loupeActive && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            [loupeSide]: 12,
            width: LOUPE,
            height: LOUPE,
            borderRadius: '50%',
            overflow: 'hidden',
            border: '3px solid rgba(255,255,255,0.95)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          <canvas ref={loupeCanvasRef} width={LOUPE} height={LOUPE} style={{ display: 'block' }} />
        </Box>
      )}
      {Boolean(uppy) && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 24,
            left: 0,
            right: 0,
            opacity: 0.9,
            borderRadius: '0 0 6px 6px',
            overflow: 'hidden',
            width,
            margin: '0 auto',
          }}
        >
          <StatusBar uppy={uppy} hideUploadButton hideAfterFinish={false} />
        </Box>
      )}
    </Box>
  );
};

CameraViewer.propTypes = {
  // @ts-ignore
  camera: propTypes.object.isRequired,
  // @ts-ignore
  uppy: propTypes.instanceOf(Uppy),
  // @ts-ignore
  sx: propTypes.object,
  rotate: propTypes.number,
  manual: propTypes.bool,
  // @ts-ignore
  manualBox: propTypes.object,
  onManualBox: propTypes.func,
  onManualBoxCommit: propTypes.func,
  onFrameSize: propTypes.func,
};
